/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  createCustomer,
  createCustomerAddress,
  getCustomerToken,
} from '@/auth/api';
import { getFormValues } from '@/auth/lib/getFormValues';
import { mergeFormObjects } from '@/auth/lib/mergeFormObjects';
import { validationUniqueSymbolsPassword } from '@/auth/lib/validationUniqueSymbolsPassword';
import { useCallback, useState } from 'preact/hooks';
import { UseSingUpFormProps } from '@/auth/types';
import { EventsList, publishEvents } from '@/auth/lib/acdl';
import { checkIsFunction } from '@/auth/lib/checkIsFunction';
import { focusOnEmptyPasswordField } from '@/auth/lib/focusOnEmptyPasswordField';

export const useSignUpForm = ({
  requireRetypePassword,
  addressesData,
  translations,
  isEmailConfirmationRequired,
  apiVersion2 = true,
  passwordConfigs,
  isAutoSignInEnabled,
  routeRedirectOnSignIn,
  routeSignIn,
  onErrorCallback,
  onSuccessCallback,
  setActiveComponent,
  handleSetInLineAlertProps,
  routeRedirectOnEmailConfirmationClose,
}: UseSingUpFormProps) => {
  const [showPasswordErrorMessage, setShowPasswordErrorMessage] =
    useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordMessage, setConfirmPasswordMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showEmailConfirmationForm, setShowEmailConfirmationForm] =
    useState(false);
  const [isSuccessful, setIsSuccessful] = useState({
    userName: '',
    status: false,
  });
  const [signUpPasswordValue, setSignUpPasswordValue] = useState('');
  const [isClickSubmit, setIsClickSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isKeepMeLogged, setIsKeepMeLogged] = useState(true);

  const onBlurPassword = useCallback(
    (event: Event) => {
      const value = (event.target as HTMLInputElement).value;

      setShowPasswordErrorMessage(!value.length);

      if (value.length && confirmPassword.length && value !== confirmPassword) {
        setConfirmPasswordMessage(translations.passwordMismatch);
      }
    },
    [confirmPassword, translations.passwordMismatch]
  );

  const onBlurConfirmPassword = useCallback(
    (event: Event) => {
      const value = (event.target as HTMLInputElement).value;

      setConfirmPasswordMessage(
        value.length ? '' : translations.requiredFieldError
      );

      if (
        value.length &&
        signUpPasswordValue.length &&
        value !== signUpPasswordValue
      ) {
        setConfirmPasswordMessage(translations.passwordMismatch);
      }
    },
    [
      signUpPasswordValue,
      translations.passwordMismatch,
      translations.requiredFieldError,
    ]
  );

  const handleConfirmPasswordChange = useCallback(
    (value: string) => {
      setConfirmPassword(value);

      if (value) {
        setConfirmPasswordMessage(
          signUpPasswordValue === value ? '' : translations.passwordMismatch
        );
      } else {
        setConfirmPasswordMessage(translations.requiredFieldError);
      }
    },
    [translations, signUpPasswordValue]
  );

  const onKeepMeLoggedChange = useCallback(({ target }: any) => {
    setIsKeepMeLogged(target.checked);
  }, []);

  const signInButton = useCallback(() => {
    if (checkIsFunction(setActiveComponent)) {
      setActiveComponent('signInForm');

      return;
    }

    if (checkIsFunction(routeSignIn)) {
      window.location.href = routeSignIn();
    }
  }, [setActiveComponent, routeSignIn]);

  const handleSetSignUpPasswordValue = useCallback(
    (value: string) => {
      setSignUpPasswordValue(value);
      setShowPasswordErrorMessage(!value.length);
      if (value === confirmPassword) {
        setConfirmPasswordMessage('');
      }
    },
    [confirmPassword]
  );

  const handleHideEmailConfirmationForm = useCallback(() => {
    handleSetInLineAlertProps();
    setSignUpPasswordValue('');

    if (checkIsFunction(routeRedirectOnEmailConfirmationClose)) {
      window.location.href = routeRedirectOnEmailConfirmationClose();
    } else {
      setShowEmailConfirmationForm(false);
      setActiveComponent?.('signInForm');
    }
  }, [
    handleSetInLineAlertProps,
    routeRedirectOnEmailConfirmationClose,
    setActiveComponent,
  ]);

  const calledLoadingAndClick = () => {
    setIsClickSubmit(true);
    setIsLoading(false);
  };

  const onInvalidFormSubmit = (event: SubmitEvent, isValid: boolean) => {
    const arePasswordsFilled =
      signUpPasswordValue.length && confirmPassword.length;
    const arePasswordsMismatched = signUpPasswordValue !== confirmPassword;

    const handleErrors = () => {
      setShowPasswordErrorMessage(!signUpPasswordValue.length);
      if (!confirmPassword) {
        setConfirmPasswordMessage(translations.requiredFieldError);
      }
      if (arePasswordsFilled && arePasswordsMismatched) {
        setConfirmPasswordMessage(translations.passwordMismatch);
      }
    };

    const handleRetypePasswordErrors = () => {
      setConfirmPasswordMessage(
        confirmPassword.length
          ? translations.passwordMismatch
          : translations.requiredFieldError
      );
      focusOnEmptyPasswordField(event, signUpPasswordValue, confirmPassword);
    };

    if (!isValid) {
      calledLoadingAndClick();
      handleErrors();
      return true;
    }

    if (
      requireRetypePassword &&
      (confirmPasswordMessage.length || arePasswordsMismatched)
    ) {
      calledLoadingAndClick();
      handleRetypePasswordErrors();
      return true;
    }

    focusOnEmptyPasswordField(event, signUpPasswordValue, '');
    handleErrors();
    return false;
  };

  const onSubmitSignUp = async (event: SubmitEvent, isValid: boolean) => {
    handleSetInLineAlertProps();
    setConfirmPasswordMessage('');

    setIsLoading(true);

    if (onInvalidFormSubmit(event, isValid)) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { confirmPasswordField, ...formValues } = getFormValues(event.target);
    const { email, password } = formValues;

    const requiredCharacterClasses =
      passwordConfigs?.requiredCharacterClasses || 0;

    const requiredPasswordLength = passwordConfigs?.minLength || 1;

    // If password validation fails - stop execution, sign-up not possible
    if (
      !validationUniqueSymbolsPassword(password, requiredCharacterClasses) ||
      requiredPasswordLength > password?.length
    ) {
      calledLoadingAndClick();

      return;
    }

    const formData = mergeFormObjects(
      {
        ...formValues,
      },
      apiVersion2
    );

    const response = await createCustomer(formData, apiVersion2);

    if (response?.errors?.length) {
      const { errors } = response;

      handleSetInLineAlertProps?.({
        type: 'error',
        text: errors[0]?.message,
      });

      onErrorCallback?.(errors);

      setUserEmail(email);
    } else {
      const createCustomerUserName = response?.firstName;

      publishEvents(EventsList.CREATE_ACCOUNT_EVENT, {
        ...response,
      });

      // If email confirmation enabled or auto sign-in disabled - stop execution, auto sign-in not possible
      if (isEmailConfirmationRequired || !isAutoSignInEnabled) {
        // Sign-up succeed, execute onSuccess callback
        onSuccessCallback?.({
          userName: createCustomerUserName,
          userEmail: email,
          status: true,
        });

        // If email confirmation enabled - show email confirmation form and stop execution
        if (isEmailConfirmationRequired) {
          (event.target as HTMLFormElement)?.reset();

          setSignUpPasswordValue('');
          setShowEmailConfirmationForm(true);
          setUserEmail(email);
          setIsLoading(false);

          return;
        }

        // If auto sign-in disabled - render success notification and stop execution
        if (!isAutoSignInEnabled) {
          setIsLoading(false);

          setIsSuccessful({
            userName: createCustomerUserName,
            status: true,
          });

          return;
        }
      }

      // Auto sign-in after sign-up
      const loginResponse = await getCustomerToken({
        email,
        password,
        translations,
        handleSetInLineAlertProps,
        onErrorCallback,
      });

      if (loginResponse?.userName) {
        if (addressesData?.length) {
          for (const address of addressesData) {
            try {
              await createCustomerAddress(address);
            } catch (error) {
              console.error(
                translations.failedCreateCustomerAddress,
                address,
                error
              );
            }
          }
        }

        onSuccessCallback?.({
          userName: loginResponse?.userName,
          userEmail: loginResponse?.userEmail,
          status: true,
        });

        if (checkIsFunction(routeRedirectOnSignIn)) {
          window.location.href = routeRedirectOnSignIn();
        } else {
          setIsSuccessful({
            userName: loginResponse?.userName,
            status: true,
          });
        }
      } else {
        // This is a fallback block, executed when registration succeed but sign-in for some reason failed
        onSuccessCallback?.({
          userName: createCustomerUserName,
          userEmail: email,
          status: true,
        });

        setIsSuccessful({
          userName: createCustomerUserName,
          status: true,
        });
      }
    }

    setIsLoading(false);
  };

  return {
    showPasswordErrorMessage,
    confirmPassword,
    confirmPasswordMessage,
    isKeepMeLogged,
    userEmail,
    showEmailConfirmationForm,
    isSuccessful,
    isClickSubmit,
    signUpPasswordValue,
    isLoading,
    onSubmitSignUp,
    signInButton,
    handleSetSignUpPasswordValue,
    onKeepMeLoggedChange,
    handleHideEmailConfirmationForm,
    handleConfirmPasswordChange,
    onBlurPassword,
    onBlurConfirmPassword,
  };
};
